import {
  AnimatePresence,
  motion,
  useScroll,
  useTransform,
} from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  Check,
  FileSearch,
  FileText,
  Globe,
  MessageSquare,
  Mic,
  Search,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { getTranslationFn, type Locale } from "../../i18n";
import { cn } from "../../lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Button } from "../ui/button";
import { MarketingCta } from "./marketing-cta";
import { MarketingLayout } from "./marketing-layout";

interface LandingPageProps {
  locale: Locale;
}

/* ------------------------------------------------------------------ */
/*  Reusable scroll-triggered fade                                     */
/* ------------------------------------------------------------------ */
function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Decorative brand triangle pattern                                  */
/* ------------------------------------------------------------------ */
function TriangleField({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 800 600"
      fill="none"
      className={cn("pointer-events-none select-none", className)}
    >
      <polygon
        points="400,50 550,350 250,350"
        className="fill-brand-500/[0.04]"
      />
      <polygon
        points="200,200 350,500 50,500"
        className="fill-brand-500/[0.03]"
      />
      <polygon
        points="600,150 750,450 450,450"
        className="fill-brand-500/[0.025]"
      />
      <polygon
        points="500,300 620,520 380,520"
        className="fill-brand-500/[0.02]"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Rotating hero text                                                 */
/* ------------------------------------------------------------------ */
function RotatingText({ texts }: { texts: string[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % texts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [texts.length]);

  return (
    <span
      className="relative inline-flex overflow-hidden align-bottom"
      style={{ height: "1.2em" }}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={index}
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: "0%", opacity: 1 }}
          exit={{ y: "-100%", opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="inline-block"
        >
          {texts[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Main landing page                                                  */
/* ------------------------------------------------------------------ */
export function LandingPage({ locale }: LandingPageProps) {
  const t = getTranslationFn(locale);
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

  // Mobile screenshot scroll-linked horizontal pan
  const screenshotRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: screenshotScrollProgress } = useScroll({
    target: screenshotRef,
    offset: ["start end", "end start"],
  });
  const screenshotX = useTransform(
    screenshotScrollProgress,
    [0, 1],
    ["0%", "-30%"]
  );

  // Disable parallax fade on mobile — always fully visible
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Use Cases — auto-advancing tabs with progress bars
  const TAB_DURATION = 5000; // ms per tab
  const [activeTab, setActiveTab] = useState(0);
  const [tabProgress, setTabProgress] = useState(0);
  const tabTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tabStartRef = useRef(Date.now());

  const useCaseTabs = [
    {
      title: t("Landing.useCases.tab1.title"),
      description: t("Landing.useCases.tab1.description"),
      icon: FileSearch,
    },
    {
      title: t("Landing.useCases.tab2.title"),
      description: t("Landing.useCases.tab2.description"),
      icon: Search,
    },
    {
      title: t("Landing.useCases.tab3.title"),
      description: t("Landing.useCases.tab3.description"),
      icon: FileText,
    },
    {
      title: t("Landing.useCases.tab4.title"),
      description: t("Landing.useCases.tab4.description"),
      icon: Mic,
    },
    {
      title: t("Landing.useCases.tab5.title"),
      description: t("Landing.useCases.tab5.description"),
      icon: MessageSquare,
    },
  ];

  const startTabTimer = useCallback(() => {
    tabStartRef.current = Date.now();
    if (tabTimerRef.current) clearInterval(tabTimerRef.current);
    tabTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - tabStartRef.current;
      const progress = Math.min(elapsed / TAB_DURATION, 1);
      setTabProgress(progress);
      if (progress >= 1) {
        setActiveTab((prev) => (prev + 1) % 5);
        tabStartRef.current = Date.now();
        setTabProgress(0);
      }
    }, 30);
  }, []);

  useEffect(() => {
    startTabTimer();
    return () => {
      if (tabTimerRef.current) clearInterval(tabTimerRef.current);
    };
  }, [startTabTimer]);

  const handleTabClick = (index: number) => {
    setActiveTab(index);
    setTabProgress(0);
    tabStartRef.current = Date.now();
  };

  const faqData = [
    { q: t("Landing.faq.q1"), a: t("Landing.faq.a1") },
    { q: t("Landing.faq.q2"), a: t("Landing.faq.a2") },
    { q: t("Landing.faq.q3"), a: t("Landing.faq.a3") },
    { q: t("Landing.faq.q4"), a: t("Landing.faq.a4") },
    { q: t("Landing.faq.q5"), a: t("Landing.faq.a5") },
    { q: t("Landing.faq.q6"), a: t("Landing.faq.a6") },
  ];

  return (
    <MarketingLayout locale={locale}>
      {/* ═══════════════════════════════════════════════════════════════
          1. HERO — Above the fold
      ═══════════════════════════════════════════════════════════════ */}
      <section
        ref={heroRef}
        className="relative flex flex-col justify-center overflow-hidden pt-12 md:pt-20"
      >
        {/* Background — subtle triangle art */}
        <TriangleField className="absolute inset-0 w-full h-full opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white/80 to-brand-50/20" />

        <div className="relative container mx-auto px-4 md:px-6 pb-16 md:pb-24">
          <div className="max-w-5xl">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="text-sm tracking-[0.3em] uppercase text-brand-600 font-medium mb-8"
            >
              {t("Landing.hero.badge")}
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="text-[clamp(2.5rem,6vw,5.5rem)] font-semibold leading-[1.15] tracking-tight text-slate-900"
            >
              <span className="text-brand-600">
                {t("Landing.hero.titlePrefix")}
              </span>{" "}
              <RotatingText
                texts={[
                  t("Landing.hero.titleRotating1"),
                  t("Landing.hero.titleRotating2"),
                  t("Landing.hero.titleRotating3"),
                ]}
              />{" "}
              <br />
              <span className="text-brand-600">
                {t("Landing.hero.titleSuffix")}
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-8 text-lg md:text-xl text-slate-500 leading-relaxed max-w-2xl font-light"
            >
              {t("Landing.hero.description")}
            </motion.p>

            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              {[
                t("Landing.hero.feature1"),
                t("Landing.hero.feature2"),
                t("Landing.hero.feature3"),
                t("Landing.hero.feature4"),
              ].map((badge) => (
                <span
                  key={badge}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-700 bg-brand-50 border border-brand-100 rounded-full"
                >
                  <Check className="w-3 h-3" />
                  {badge}
                </span>
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-12"
            >
              <Button
                size="lg"
                asChild
                className="h-14 px-10 text-base font-medium rounded-full bg-brand-600 text-white shadow-2xl shadow-brand-600/20 hover:shadow-brand-600/30 transition-all hover:-translate-y-0.5 hover:bg-brand-700"
              >
                <Link to="mailto:hello@anylawyer.com">
                  {t("Landing.hero.bookDemo")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          2. SCREENSHOT (sticky — next section scrolls over it)
      ═══════════════════════════════════════════════════════════════ */}
      <div className="relative" ref={screenshotRef}>
        <div className="sticky top-0 z-0 pb-24 md:pb-32 overflow-hidden">
          <motion.div
            style={isMobile ? { x: screenshotX } : { y: heroImgY }}
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
            viewport={{ once: true, amount: 0.1 }}
            className="px-2 md:container md:mx-auto md:px-6"
          >
            <div className="relative mx-auto md:max-w-none">
              <img
                src="/assets/side-by-side-v3.png"
                alt="AnyLawyer Interface"
                className="rounded-xl md:rounded-2xl shadow-[0_40px_100px_-20px_rgba(0,0,0,0.12)] border border-slate-200/50 w-[180%] max-w-none md:w-full"
              />
              <div className="absolute -inset-px rounded-xl md:rounded-2xl bg-gradient-to-t from-white/20 to-transparent pointer-events-none" />
            </div>
          </motion.div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════
            3. SOCIAL PROOF — micro-stats
        ═══════════════════════════════════════════════════════════════ */}
        <section className="relative z-10 py-20 md:py-24 bg-brand-900">
          <div className="container mx-auto px-4">
            <Reveal>
              <p className="text-center text-sm text-brand-300/70 font-medium uppercase tracking-widest mb-12">
                {t("Landing.socialProof.badge")}
              </p>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-16 max-w-3xl mx-auto">
              {[
                {
                  value: t("Landing.socialProof.stat1.value"),
                  label: t("Landing.socialProof.stat1.label"),
                },
                {
                  value: t("Landing.socialProof.stat2.value"),
                  label: t("Landing.socialProof.stat2.label"),
                },
              ].map((stat, i) => (
                <Reveal key={stat.label} delay={i * 0.1}>
                  <div className="text-center">
                    <div className="text-5xl md:text-7xl font-bold text-white tracking-tight">
                      {stat.value}
                    </div>
                    <div className="mt-3 text-sm md:text-base text-brand-200/70 font-light">
                      {stat.label}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          4. PROBLEM → SOLUTION
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 max-w-5xl mx-auto items-stretch">
            {/* Problem side */}
            <Reveal>
              <div className="p-8 md:p-12 md:pr-16 h-full">
                <span className="inline-block text-xs font-medium uppercase tracking-widest text-slate-400 mb-6">
                  {t("Landing.problem.title")}
                </span>
                <ul className="space-y-5">
                  {[
                    t("Landing.problem.item1"),
                    t("Landing.problem.item2"),
                    t("Landing.problem.item3"),
                    t("Landing.problem.item4"),
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-slate-500 leading-relaxed"
                    >
                      <span className="mt-2 w-1 h-1 rounded-full bg-slate-300 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Solution side */}
            <Reveal delay={0.1}>
              <div className="p-8 md:p-12 md:pl-16 h-full border-t md:border-t-0 md:border-l border-slate-200">
                <span className="inline-block text-xs font-medium uppercase tracking-widest text-brand-600 mb-6">
                  {t("Landing.solution.title")}
                </span>
                <ul className="space-y-5">
                  {[
                    t("Landing.solution.item1"),
                    t("Landing.solution.item2"),
                    t("Landing.solution.item3"),
                    t("Landing.solution.item4"),
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-slate-700 leading-relaxed"
                    >
                      <span className="mt-2 w-1 h-1 rounded-full bg-brand-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          5. KILLER FEATURE — Polish legal acts
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-brand-900" id="features">
        <div className="container mx-auto px-4">
          <Reveal className="max-w-2xl mb-20 ml-4 md:ml-8">
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white leading-[1.1]">
              {t("Landing.killerFeature.title")}
            </h2>
            <p className="mt-6 text-lg text-brand-200/70 leading-relaxed font-light">
              {t("Landing.killerFeature.description")}
            </p>
          </Reveal>

          {/* Source icons grid */}
          <div className="grid md:grid-cols-3 gap-16 md:gap-20 max-w-6xl ml-4 md:ml-8">
            {[
              {
                icon: BookOpen,
                label: t("Landing.killerFeature.source1"),
              },
              {
                icon: FileText,
                label: t("Landing.killerFeature.source2"),
              },
              {
                icon: Globe,
                label: t("Landing.killerFeature.source4"),
              },
            ].map((source, i) => (
              <Reveal key={source.label} delay={i * 0.1}>
                <div>
                  <source.icon className="w-8 h-8 text-brand-300 mb-4" />
                  <h3 className="text-xl font-semibold text-white tracking-tight">
                    {source.label}
                  </h3>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="ml-4 md:ml-8 mt-12">
            <p className="text-sm text-brand-300/60 italic font-light">
              {t("Landing.killerFeature.microcopy")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          6. USE CASES — tabs
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32" id="use-cases">
        <div className="container mx-auto px-4">
          <Reveal className="text-center max-w-4xl mx-auto mb-16">
            <p className="text-xs tracking-[0.35em] uppercase text-brand-600 font-medium mb-6">
              {t("Landing.benefits.title")}
            </p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
              {t("Landing.useCases.title")}
            </h2>
          </Reveal>

          <Reveal className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-x-4 gap-y-6 md:gap-x-0 md:gap-y-0 items-start">
              {useCaseTabs.map((tab, i) => {
                const isActive = activeTab === i;
                const isPast = i < activeTab;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleTabClick(i)}
                    className="text-left px-2 md:px-6 cursor-pointer"
                  >
                    {/* Progress bar */}
                    <div className="h-0.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-600 rounded-full"
                        style={{
                          width: isPast
                            ? "100%"
                            : isActive
                              ? `${tabProgress * 100}%`
                              : "0%",
                          transition: isActive ? "none" : "width 0.3s ease",
                        }}
                      />
                    </div>
                    <h3
                      className={cn(
                        "text-sm font-semibold tracking-tight transition-colors pt-3 md:pt-5",
                        isActive || isPast ? "text-slate-900" : "text-slate-400"
                      )}
                    >
                      {tab.title}
                    </h3>
                  </button>
                );
              })}
            </div>

            {/* Active tab content */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-6 p-6 md:p-12"
            >
              <div className="flex items-start gap-4">
                {(() => {
                  const ActiveIcon = useCaseTabs[activeTab].icon;
                  return (
                    <ActiveIcon className="w-8 h-8 text-brand-600 flex-shrink-0 mt-1" />
                  );
                })()}
                <div>
                  <h3 className="text-2xl font-semibold text-slate-900 tracking-tight mb-4">
                    {useCaseTabs[activeTab].title}
                  </h3>
                  <p className="text-lg text-slate-500 leading-relaxed font-light">
                    {useCaseTabs[activeTab].description}
                  </p>
                </div>
              </div>
            </motion.div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          7. TRUST & ANTI-HALLUCINATION — 3 pillars
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="container mx-auto px-4">
          <Reveal className="max-w-2xl mb-20 ml-4 md:ml-8">
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
              {t("Landing.trust.title")}
            </h2>
            <p className="mt-6 text-lg text-slate-400 leading-relaxed font-light">
              {t("Landing.trust.description")}
            </p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-16 md:gap-20 max-w-6xl ml-4 md:ml-8">
            {[
              {
                title: t("Landing.trust.citations.title"),
                body: t("Landing.trust.citations.description"),
                num: "01",
              },
              {
                title: t("Landing.trust.auditTrail.title"),
                body: t("Landing.trust.auditTrail.description"),
                num: "02",
              },
              {
                title: t("Landing.trust.depthControl.title"),
                body: t("Landing.trust.depthControl.description"),
                num: "03",
              },
            ].map((item, i) => (
              <Reveal key={item.num} delay={i * 0.1}>
                <div>
                  <span className="text-6xl md:text-7xl font-bold text-brand-100 leading-none select-none">
                    {item.num}
                  </span>
                  <h3 className="mt-4 text-xl font-semibold text-slate-900 tracking-tight">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-slate-500 leading-relaxed font-light">
                    {item.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          8. HOW IT WORKS
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-brand-900" id="how-it-works">
        <div className="container mx-auto px-4">
          <Reveal className="max-w-2xl mb-20 ml-4 md:ml-8">
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white leading-[1.1]">
              {t("Landing.process.headline")}
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-16 md:gap-20 max-w-6xl ml-4 md:ml-8">
            {[
              {
                step: "01",
                title: t("Landing.howItWorks.step1.title"),
                body: t("Landing.howItWorks.step1.description"),
              },
              {
                step: "02",
                title: t("Landing.howItWorks.step2.title"),
                body: t("Landing.howItWorks.step2.description"),
              },
              {
                step: "03",
                title: t("Landing.howItWorks.step3.title"),
                body: t("Landing.howItWorks.step3.description"),
              },
            ].map((item, i) => (
              <Reveal key={item.step} delay={i * 0.1}>
                <div>
                  <span className="text-6xl md:text-7xl font-bold text-brand-800 leading-none select-none">
                    {item.step}
                  </span>
                  <h3 className="mt-4 text-xl font-semibold text-white tracking-tight">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-brand-200/70 leading-relaxed font-light">
                    {item.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TODO: Testimonials section — hidden until real data is available */}

      {/* ═══════════════════════════════════════════════════════════════
          11. FAQ
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-slate-50" id="faq">
        <div className="container mx-auto px-4">
          <Reveal className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-3">
              {t("Landing.faq.title")} {t("Landing.faq.title2")}{" "}
              {t("Landing.faq.title3")}
            </h2>
            <p className="text-slate-500 font-light">
              {t("Landing.faq.subtitle")}
            </p>
          </Reveal>

          <Reveal className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqData.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-base">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          12. FINAL CTA
      ═══════════════════════════════════════════════════════════════ */}
      <MarketingCta locale={locale} />
    </MarketingLayout>
  );
}
